import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Create a cache for commission data
const commissionDataCaches = {
  SELLER: {
    data: null,
    timestamp: null,
    loading: false,
    callbacks: [],
  },
  BUYER: {
    data: null,
    timestamp: null,
    loading: false,
    callbacks: [],
  },
};

/**
 * Custom hook to fetch commission data with global caching
 * Ensures only one API call is made across multiple component instances
 * @param {Object} auth - Authentication context
 * @param {String} type - "SELLER" or "BUYER"
 * @returns {Object} - Data, loading state, and refresh function
 */
const useCommissionData = (auth, type = 'SELLER') => {
  const cacheType = type === 'BUYER' ? 'BUYER' : 'SELLER';
  const cache = commissionDataCaches[cacheType];

  const [data, setData] = useState(cache.data);
  const [loading, setLoading] = useState(cache.loading);

  const fetchData = useCallback(async () => {
    // If data is already being fetched, add this component's setState to callbacks
    if (cache.loading) {
      cache.callbacks.push(setData);
      setLoading(true);
      return;
    }

    // If we already have data and it's less than 5 minutes old, use cached data
    const now = Date.now();
    if (cache.data && cache.timestamp && now - cache.timestamp < 300000) {
      // 5 minutes cache
      setData(cache.data);
      setLoading(false);
      return;
    }

    // Otherwise, fetch new data
    if (!auth?.data?.token) return;

    cache.loading = true;
    setLoading(true);

    try {
      const endpoint =
        type === 'BUYER'
          ? 'commission/fetch-all-commission-slabs-Buyer'
          : 'commission/fetch-all-commission-slabs';

      const response = await axios.get(
        `${process.env.REACT_APP_REST_API_ENDPOINT}${endpoint}`,
        {
          headers: {
            Authorization: `Bearer ${auth.data.token}`,
          },
        },
      );

      // Update cache
      cache.data = response.data;
      cache.timestamp = Date.now();

      // Update all waiting components
      setData(response.data);
      cache.callbacks.forEach((callback) => callback(response.data));
      cache.callbacks = [];
    } catch (err) {
      console.error(`Error fetching commission data for ${type}:`, err);
    } finally {
      cache.loading = false;
      setLoading(false);
    }
  }, [auth, type, cache]);

  useEffect(() => {
    if (auth?.data?.token) {
      fetchData();
    }

    return () => {
      // Remove this component's setState from callbacks if unmounted
      cache.callbacks = cache.callbacks.filter(
        (callback) => callback !== setData,
      );
    };
  }, [auth?.data?.token, fetchData, cache]);

  return { data, loading, refreshData: fetchData };
};

export default useCommissionData;
